import {
	Accordion,
	Alert,
	Badge,
	Box,
	Button,
	Grid,
	Image as MantineImage,
	Popover,
	Text,
	Title,
	Tooltip,
	Rating,
	TextInput,
	LoadingOverlay,
} from '@mantine/core'
import Link from 'next/link'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { IconCertificate, IconExternalLink } from '@tabler/icons-react'
import { useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from './../../state/app-store'

import { extractYoutubeId, sluggify } from 'lib/helper'
import { getUploadThumbPath } from '../../lib/display'
import * as AnalyitcsUtil from 'lib/analytics'
import * as Types from '../../declarations'
import * as Actions from './../../state/actions'
import SectionHeader from './components/SectionHeader'
import Video from 'components/Video'
import SignUpNudge from 'components/SignUpNudge'
import { ACCENT_COLOUR, THEME_COLOURS } from 'pages/_app'
import * as Selectors from './../../state/selectors'

interface Props {
	trip: Types.DB.Models.TripAttributes
	version: Types.VERSION
	isAdSource: boolean
}

const GuideOfferingOverview = ({
	guideTrip,
	tripDetails,
}: {
	guideTrip: Types.API.GuideTrip
	tripDetails: TripDetailsHighlight
}) => {
	return (
		<>
			<div className="guide-offering-overview">
				<div className="guide-offering-overview-small">
					<div className="logo-column">
						{guideTrip.guideCompany?.jsonProfile.logoUploadId && (
							<Image
								src={getUploadThumbPath(
									guideTrip.guideCompany?.jsonProfile.logoUploadId,
									'small',
								)}
								alt={`${guideTrip.guideCompany.companyName} logo`}
								width={28}
								height={28}
								style={{
									borderRadius: 8,
									objectFit: 'cover',
								}}
							/>
						)}
					</div>

					<div className="company-name-column guide-name">
						<Link
							href={`/guides/${sluggify(
								guideTrip.guideCompany?.companyName as string,
							)}`}
						>
							{guideTrip.guideCompany?.companyName}
							{guideTrip.guideCompany?.isPremium && (
								<span
									style={{
										color: 'gold',
										marginLeft: '4px',
									}}
									title="Recommended Guide"
								>
									★
								</span>
							)}
						</Link>
					</div>

					<div className="review-column">
						{!!guideTrip.guideCompany?.companyReviewData
							?.totalReviewAverage && (
							<>
								<Tooltip
									label={`Average rating: ${
										guideTrip.guideCompany.companyReviewData
											.totalReviewAverage / 20
									}/5 over ${
										guideTrip.guideCompany.companyReviewData.totalReviewCount
									} reviews`}
								>
									<Box>
										<span>
											{guideTrip.guideCompany.companyReviewData
												.totalReviewAverage / 20}{' '}
											/ 5
										</span>
									</Box>
								</Tooltip>
							</>
						)}
					</div>

					<div className="price-column">
						<strong>${(+guideTrip.price).toLocaleString()}</strong>
						{!!guideTrip.guideCompany?.jsonProfile.discounts && (
							<>
								{/* <Popover
									position="bottom-end"
									withArrow
									shadow="md"
									arrowSize={16}
								>
									<Popover.Target> */}
								<Badge radius="sm" variant="filled">
									<div
										style={{
											display: 'inline-flex',
											alignItems: 'center',
											gap: '4px',
											cursor: 'zoom-in',
										}}
									>
										{/* <PigMoney size={12} style={{}} /> */}
										Discounts 👀
									</div>
								</Badge>
								{/* </Popover.Target>
									<Popover.Dropdown>
										{guideTrip.guideCompany?.jsonProfile.discounts}
									</Popover.Dropdown>
								</Popover> */}
							</>
						)}
					</div>
				</div>
				<div className="guide-offering-overview-medium">
					<div className="logo-column">
						{guideTrip.guideCompany?.jsonProfile.logoUploadId && (
							<Image
								src={getUploadThumbPath(
									guideTrip.guideCompany?.jsonProfile.logoUploadId,
									'small',
								)}
								alt={`${guideTrip.guideCompany.companyName} logo`}
								width={28}
								height={28}
								style={{
									borderRadius: 8,
									objectFit: 'cover',
								}}
							/>
						)}
					</div>

					<div className="company-name-review-column">
						<div className="guide-name">
							<Link
								href={`/guides/${sluggify(
									guideTrip.guideCompany?.companyName as string,
								)}`}
							>
								{guideTrip.guideCompany?.companyName}
							</Link>
						</div>
						{!!guideTrip.guideCompany?.companyReviewData
							?.totalReviewAverage && (
							<>
								<Tooltip
									label={`Average rating: ${
										guideTrip.guideCompany.companyReviewData
											.totalReviewAverage / 20
									}/5 over ${
										guideTrip.guideCompany.companyReviewData.totalReviewCount
									} reviews`}
								>
									<Box
										style={{
											display: 'flex',
											flexDirection: 'row',
											alignItems: 'center',
											gap: '8px',
										}}
									>
										<Rating
											value={
												guideTrip.guideCompany.companyReviewData
													.totalReviewAverage / 20
											}
											fractions={10}
											readOnly
											size="xs"
										/>
										<span>
											{guideTrip.guideCompany.companyReviewData
												.totalReviewAverage / 20}{' '}
											/ 5
										</span>
									</Box>
								</Tooltip>
							</>
						)}
					</div>

					<div className="options-column">
						<div className="detail-options">
							<div className="individual-option">
								<Tooltip label="🎒 Includes All Gear / Equipment">
									<Box>{tripDetails.INCLUDES_GEAR && <>🎒</>}</Box>
								</Tooltip>
							</div>
							<div className="individual-option">
								<Tooltip label="🚶 Includes Porters">
									<Box>{tripDetails.INCLUDES_PORTERS && <>🚶</>}</Box>
								</Tooltip>
							</div>
							<div className="individual-option">
								<Tooltip
									label={`👥 Group size: ` + tripDetails.TYPICAL_GROUP_SIZE}
								>
									<Box>{!!tripDetails.TYPICAL_GROUP_SIZE && <>👥</>}</Box>
								</Tooltip>
							</div>
							<div className="individual-option">
								<Tooltip
									label={`🧑‍🤝‍🧑 Climber to guide ratio: 1 guide to ${tripDetails.CLIMBER_GUIDE_RATIO.join(
										'-',
									)} client(s)`}
								>
									<Box>
										{tripDetails.CLIMBER_GUIDE_RATIO.length > 0 && <>🧑‍🤝‍🧑</>}
									</Box>
								</Tooltip>
							</div>
							<div className="individual-option">
								<Tooltip
									label={`⏳ Trip length: ${tripDetails.TRIP_LENGTH.join(
										'-',
									)} days`}
								>
									<Box>{tripDetails.TRIP_LENGTH.length > 0 && <>⏳</>}</Box>
								</Tooltip>
							</div>
							<div className="individual-option">
								<Tooltip
									label={`📜 Guide has ${tripDetails.CERTIFICATIONS.length} certification(s)`}
								>
									<Box>{tripDetails.CERTIFICATIONS.length > 0 && <>📜</>}</Box>
								</Tooltip>
							</div>

							<div className="individual-option">
								<Tooltip
									label={`🎥 Guide has ${tripDetails.VIDEOS} video(s) about the trip`}
								>
									<Box>{tripDetails.VIDEOS > 0 && <>🎥</>}</Box>
								</Tooltip>
							</div>
							<div className="individual-option">
								<Tooltip label={`📝 Read the guides Description`}>
									<Box>{tripDetails.BLURB && <>📝</>}</Box>
								</Tooltip>
							</div>

							{/* // todo: images */}
						</div>
					</div>

					{/* <div className="review-column">
						{!!guideTrip.guideCompany?.companyReviewData
							?.totalReviewAverage && (
							<>
								<Tooltip
									label={`Average rating: ${
										guideTrip.guideCompany.companyReviewData
											.totalReviewAverage / 20
									}/5 over ${
										guideTrip.guideCompany.companyReviewData.totalReviewCount
									} reviews`}
								>
									<Box>
										<Rating
											value={
												guideTrip.guideCompany.companyReviewData
													.totalReviewAverage / 20
											}
											fractions={10}
											readOnly
											size="xs"
										/>
										<span>
											{guideTrip.guideCompany.companyReviewData
												.totalReviewAverage / 20}{' '}
											/ 5
										</span>
									</Box>
								</Tooltip>
							</>
						)}
					</div> */}

					<div className="price-column">
						<strong>${(+guideTrip.price).toLocaleString()}</strong>
						{!!guideTrip.guideCompany?.jsonProfile.discounts && (
							<>
								{/* <Popover
									position="bottom-end"
									withArrow
									shadow="md"
									arrowSize={16}
								>
									<Popover.Target> */}
								<Badge radius="sm" variant="filled">
									<div
										style={{
											display: 'inline-flex',
											alignItems: 'center',
											gap: '4px',
											cursor: 'zoom-in',
										}}
									>
										{/* <PigMoney size={12} style={{}} /> */}
										Discounts 👀
									</div>
								</Badge>
								{/* </Popover.Target>
									<Popover.Dropdown>
										{guideTrip.guideCompany?.jsonProfile.discounts}
									</Popover.Dropdown> 
								</Popover>*/}
							</>
						)}
					</div>

					{/* <div className="link-column">
						<a
							href={`/api/link?pGuideTripIdClicked=${guideTrip.id}`}
							target="_blank"
							rel="nofollow noreferrer ugc"
							onMouseDown={() => {
								AnalyitcsUtil.linkClickEvent()
							}}
						>
							<span className="external-trip-link-text">
								www.
								{guideTrip.guideCompany?.jsonProfile.website
									?.replace('https://', '')
									.replace('www.', '')}
							</span>{' '}
							<IconExternalLink size={14} style={{ marginLeft: '4px' }} />
						</a>
					</div> */}
				</div>
				<div className="guide-offering-overview-large">
					<div className="logo-column">
						{guideTrip.guideCompany?.jsonProfile.logoUploadId && (
							<Image
								src={getUploadThumbPath(
									guideTrip.guideCompany?.jsonProfile.logoUploadId,
									'small',
								)}
								alt={`${guideTrip.guideCompany.companyName} logo`}
								width={28}
								height={28}
								style={{
									borderRadius: 8,
									objectFit: 'cover',
								}}
							/>
						)}
					</div>

					<div className="company-name-column guide-name">
						<Link
							href={`/guides/${sluggify(
								guideTrip.guideCompany?.companyName as string,
							)}`}
						>
							{guideTrip.guideCompany?.companyName}
							{guideTrip.guideCompany?.isPremium && (
								<span
									style={{
										color: 'gold',
										marginLeft: '4px',
									}}
									title="Recommended Guide"
								>
									★
								</span>
							)}
						</Link>
					</div>

					<div className="options-column">
						<div className="detail-options">
							<div className="individual-option">
								<Tooltip label="🎒 Includes All Gear / Equipment">
									<Box>{tripDetails.INCLUDES_GEAR && <>🎒</>}</Box>
								</Tooltip>
							</div>
							<div className="individual-option">
								<Tooltip label="🚶 Includes Porters">
									<Box>{tripDetails.INCLUDES_PORTERS && <>🚶</>}</Box>
								</Tooltip>
							</div>
							<div className="individual-option">
								<Tooltip
									label={`👥 Group size: ` + tripDetails.TYPICAL_GROUP_SIZE}
								>
									<Box>{!!tripDetails.TYPICAL_GROUP_SIZE && <>👥</>}</Box>
								</Tooltip>
							</div>
							<div className="individual-option">
								<Tooltip
									label={`🧑‍🤝‍🧑 Climber to guide ratio: 1 guide to ${tripDetails.CLIMBER_GUIDE_RATIO.join(
										'-',
									)} client(s)`}
								>
									<Box>
										{tripDetails.CLIMBER_GUIDE_RATIO.length > 0 && <>🧑‍🤝‍🧑</>}
									</Box>
								</Tooltip>
							</div>
							<div className="individual-option">
								<Tooltip
									label={`⏳ Trip length: ${tripDetails.TRIP_LENGTH.join(
										'-',
									)} days`}
								>
									<Box>{tripDetails.TRIP_LENGTH.length > 0 && <>⏳</>}</Box>
								</Tooltip>
							</div>
							<div className="individual-option">
								<Tooltip
									label={`📜 Guide has ${tripDetails.CERTIFICATIONS.length} certification(s)`}
								>
									<Box>{tripDetails.CERTIFICATIONS.length > 0 && <>📜</>}</Box>
								</Tooltip>
							</div>

							<div className="individual-option">
								<Tooltip
									label={`🎥 Guide has ${tripDetails.VIDEOS} video(s) about the trip`}
								>
									<Box>{tripDetails.VIDEOS > 0 && <>🎥</>}</Box>
								</Tooltip>
							</div>
							<div className="individual-option">
								<Tooltip label={`📝 Read the guides Description`}>
									<Box>{tripDetails.BLURB && <>📝</>}</Box>
								</Tooltip>
							</div>

							{/* // todo: images */}
						</div>
					</div>

					<div className="review-column">
						{!!guideTrip.guideCompany?.companyReviewData
							?.totalReviewAverage && (
							<>
								<Tooltip
									label={`Average rating: ${
										guideTrip.guideCompany.companyReviewData
											.totalReviewAverage / 20
									}/5 over ${
										guideTrip.guideCompany.companyReviewData.totalReviewCount
									} reviews`}
								>
									<Box>
										<Rating
											value={
												guideTrip.guideCompany.companyReviewData
													.totalReviewAverage / 20
											}
											fractions={10}
											readOnly
											size="xs"
										/>
										<span>
											{guideTrip.guideCompany.companyReviewData
												.totalReviewAverage / 20}{' '}
											/ 5
										</span>
									</Box>
								</Tooltip>
							</>
						)}
					</div>

					<div className="price-column">
						<strong>${(+guideTrip.price).toLocaleString()}</strong>
						{!!guideTrip.guideCompany?.jsonProfile.discounts && (
							<>
								{/* <Popover
									position="bottom-end"
									withArrow
									shadow="md"
									arrowSize={16}
								>
									<Popover.Target>*/}
								<Badge radius="sm" variant="filled">
									<div
										style={{
											display: 'inline-flex',
											alignItems: 'center',
											gap: '4px',
											cursor: 'zoom-in',
										}}
									>
										{/* <PigMoney size={12} style={{}} /> */}
										Discounts 👀
									</div>
								</Badge>
								{/*	</Popover.Target>
									<Popover.Dropdown>
										{guideTrip.guideCompany?.jsonProfile.discounts}
									</Popover.Dropdown>
								</Popover> */}
							</>
						)}
					</div>

					<div className="link-column">
						<a
							href={`/api/link?pGuideTripIdClicked=${guideTrip.id}`}
							target="_blank"
							rel="nofollow noreferrer ugc"
							onMouseDown={() => {
								AnalyitcsUtil.linkClickEvent()
							}}
						>
							<span className="external-trip-link-text">
								www.
								{guideTrip.guideCompany?.jsonProfile.website
									?.replace('https://', '')
									.replace('www.', '')}
							</span>{' '}
							<IconExternalLink size={14} style={{ marginLeft: '4px' }} />
						</a>
					</div>
				</div>
			</div>
		</>
	)
}

type TripDetailsHighlight = {
	INCLUDES_GEAR: boolean
	INCLUDES_PORTERS: boolean
	TYPICAL_GROUP_SIZE: string
	CLIMBER_GUIDE_RATIO: number[]
	TRIP_LENGTH: number[]
	CERTIFICATIONS: Types.API.Types.GuideCertificate[]
	VIDEOS: number
	IMAGES: number
	BLURB: boolean
}

const GuideTable = ({
	guideTrips,
	version,
}: {
	guideTrips: Types.API.GuideTrip[]
	version: Types.VERSION
}) => {
	const [sortBy, setSortBy] = useState<'price' | 'rating' | null>(null)
	const [sortDesc, setSortDesc] = useState(true)

	const TRIP_LIMIT = 15
	const totalTrips = guideTrips.length
	const [isShowingAll, setIsShowingAll] = useState<boolean>(
		totalTrips <= TRIP_LIMIT,
	)

	const sortedGuideTrips = useMemo(() => {
		if (!sortBy) return guideTrips
		return [...guideTrips].sort((a, b) => {
			if (sortBy === 'price') {
				return sortDesc ? b.price - a.price : a.price - b.price
			}
			// Sort by rating
			const ratingA = a.guideCompany?.companyReviewData?.totalReviewAverage ?? 0
			const ratingB = b.guideCompany?.companyReviewData?.totalReviewAverage ?? 0
			return sortDesc ? ratingB - ratingA : ratingA - ratingB
		})
	}, [guideTrips, sortBy, sortDesc])

	const toggleSort = (field: 'price' | 'rating') => {
		if (sortBy === field) {
			setSortDesc(!sortDesc)
		} else {
			setSortBy(field)
			// Default to ascending (false) for price, descending (true) for rating
			setSortDesc(field === 'rating')
		}
	}

	const GOLD_GRADIENT = 'linear-gradient(180deg, #B8860B, #FFD700, #B8860B)'
	const GOLD_BORDER = '#FFD700'
	const TRANSPARENT_BORDER = 'transparent'

	return (
		<div id="guides-table">
			<Accordion>
				<div
					style={{
						display: 'flex',
						gap: '1rem',
						marginBottom: '1rem',
						justifyContent: 'flex-end',
					}}
				>
					<Button
						variant={sortBy === 'price' ? 'filled' : 'outline'}
						onClick={() => toggleSort('price')}
						rightSection={
							sortBy === 'price' ? (sortDesc ? '↓' : '↑') : undefined
						}
						className="plausible-event-name=Guides-sort-by-price-click"
					>
						{sortBy === 'price' ? 'Sorted by Price' : 'Sort by Price'}
					</Button>
					<Button
						variant={sortBy === 'rating' ? 'filled' : 'outline'}
						onClick={() => toggleSort('rating')}
						rightSection={
							sortBy === 'rating' ? (sortDesc ? '↓' : '↑') : undefined
						}
						className="plausible-event-name=Guides-sort-by-rating-click"
					>
						{sortBy === 'rating' ? 'Sorted by Rating' : 'Sort by Rating'}
					</Button>
				</div>

				{sortedGuideTrips
					// show max TRIP_LIMIT, or all
					.filter(
						(trip, idxTrip) =>
							isShowingAll || (!isShowingAll && idxTrip < TRIP_LIMIT),
					)
					.map((guideTrip, idxGuideTrip) => {
						const tripDetails: TripDetailsHighlight = {
							INCLUDES_GEAR: !!guideTrip.jsonDetails?.includesGear,
							INCLUDES_PORTERS: !!guideTrip.jsonDetails?.includesPorters,
							TYPICAL_GROUP_SIZE:
								(guideTrip.jsonDetails?.typicalGroupSize ?? []).length > 0
									? (guideTrip.jsonDetails?.typicalGroupSize as number[])?.join(
											' - ',
									  )
									: '',
							CLIMBER_GUIDE_RATIO:
								guideTrip.jsonDetails?.climberToGuideRatio ?? [],
							TRIP_LENGTH:
								guideTrip.jsonDetails?.lengtOfTripInDays?.filter(
									(val) => !!val,
								) ?? [],
							CERTIFICATIONS: (guideTrip.guideCompany?.certificates ??
								[]) as Types.API.Types.GuideCertificate[],

							VIDEOS: (guideTrip.jsonDetails?.videos ?? []).length,
							// todo: later
							IMAGES: 0,
							BLURB: !!guideTrip.jsonDetails?.shortBlurb,
						}

						const isAccordionItem: boolean =
							(!!guideTrip?.jsonDetails &&
								Object.values(guideTrip?.jsonDetails).filter((val) => {
									// Handle arrays - consider empty arrays as falsy
									if (Array.isArray(val)) {
										return val.length > 0
									}
									// Handle other values normally
									return !!val
								}).length > 0) ||
							(guideTrip.guideCompany?.certificates ?? []).length > 0 ||
							!!guideTrip.guideCompany?.jsonProfile?.discounts

						const isPremium = !!guideTrip.guideCompany?.isPremium

						return isAccordionItem ? (
							<div
								key={`item-${idxGuideTrip}`}
								style={{
									borderLeft: isPremium
										? '4px solid #FFD700'
										: '4px solid transparent',
								}}
							>
								<Accordion.Item value={`item-${idxGuideTrip}`}>
									<Accordion.Control className="custom-accordion-label">
										<GuideOfferingOverview
											guideTrip={guideTrip}
											tripDetails={tripDetails}
										/>
									</Accordion.Control>
									<Accordion.Panel
										style={{
											background: '#FCF8F3',
											boxShadow:
												'inset 0 6px 8px -8px #e2d6c2, inset 0 -6px 8px -8px #e2d6c2',
										}}
									>
										{/* [top/bottom] */}
										{!!guideTrip?.jsonDetails?.shortBlurb && (
											<Text mb="md">{guideTrip.jsonDetails.shortBlurb}</Text>
										)}

										<Box
											style={{
												display: 'flex',
												flexDirection: 'row',
												flexWrap: 'wrap',
												rowGap: '4px',
												gap: '16px',
											}}
											mb="md"
											pt="sm"
										>
											{/* first column of bullets */}
											{tripDetails.INCLUDES_PORTERS && (
												<div>🚶 Includes Porters</div>
											)}
											{tripDetails.INCLUDES_GEAR && (
												<div>🎒 Includes All Gear / Equipment</div>
											)}
											{!!tripDetails.TYPICAL_GROUP_SIZE && (
												<div>
													👥 Group size: {tripDetails.TYPICAL_GROUP_SIZE}
												</div>
											)}
											{tripDetails.CLIMBER_GUIDE_RATIO.length > 0 && (
												<div>
													🧑‍🤝‍🧑 Guide ratio: 1 guide to{' '}
													{tripDetails.CLIMBER_GUIDE_RATIO.join('-')} client(s)
												</div>
											)}
											{tripDetails.TRIP_LENGTH.length > 0 && (
												<div>
													⏳ Trip length {tripDetails.TRIP_LENGTH.join('-')}{' '}
													day(s)
												</div>
											)}
										</Box>
										{(guideTrip.jsonDetails?.videos ?? []).length > 0 && (
											<Box mb="md">
												{/* // maybe one third column */}
												{/* [videos] */}

												<div
													style={{
														display: 'grid',
														gridTemplateColumns:
															'repeat(auto-fill, minmax(200px, 1fr))', // Automatically adjust columns
														gap: '12px', // Adjust the gap as needed
													}}
												>
													{guideTrip.jsonDetails?.videos?.map(
														(videoURL, idxVideo) => (
															<div key={idxVideo}>
																<Video
																	id={extractYoutubeId(videoURL) as string}
																	title={`Video ${idxVideo + 1}`}
																/>
															</div>
														),
													)}
												</div>
											</Box>
										)}

										{((guideTrip.guideCompany?.certificates ?? []).length > 0 ||
											!!guideTrip.guideCompany?.jsonProfile
												?.tripAdvisorReviewURL ||
											!!guideTrip.guideCompany?.jsonProfile?.discounts) && (
											<>
												<Title order={5} mb="sm">
													More info on {guideTrip.guideCompany?.companyName}:
												</Title>
											</>
										)}
										{(guideTrip.guideCompany?.certificates ?? []).length >
											0 && (
											<Box mb="md">
												{/* <Title order={5}>Certifications:</Title> */}
												<div
													style={{
														display: 'flex',
														gap: '8px',
														flexWrap: 'wrap',
													}}
												>
													{(guideTrip.guideCompany?.certificates ?? [])?.map(
														(cert, idxCert) => (
															<Popover
																key={idxCert}
																position="bottom"
																withArrow
																shadow="md"
																arrowSize={16}
															>
																<Popover.Target>
																	<div
																		style={{
																			border: '1px solid silver',
																			borderRadius: '6px',
																			padding: '2px 6px',
																			display: 'flex',
																			alignItems: 'center',
																			gap: '8px',
																			cursor: 'zoom-in',
																		}}
																	>
																		<IconCertificate
																			size={20}
																			style={{
																				verticalAlign: 'middle',
																				lineHeight: '100%',
																			}}
																		/>

																		<span>
																			{
																				(cert as Types.API.Types.GuideCertificate)
																					.certificateName
																			}
																		</span>
																	</div>
																</Popover.Target>
																<Popover.Dropdown>
																	<MantineImage
																		style={{
																			maxWidth: '80vw',
																			height: 'auto',
																			maxHeight: '80vw',
																		}}
																		src={getUploadThumbPath(
																			(cert as Types.API.Types.GuideCertificate)
																				.imageId,
																			'medium',
																		)}
																		radius="md"
																	/>
																</Popover.Dropdown>
															</Popover>
														),
													)}
												</div>
											</Box>
										)}
										{(!!guideTrip.guideCompany?.jsonProfile
											?.tripAdvisorReviewURL ||
											!!guideTrip.guideCompany?.jsonProfile?.discounts) && (
											<>
												<Grid>
													{!!guideTrip.guideCompany?.companyReviewData
														?.totalReviewAverage && (
														<Grid.Col span={{ base: 12, md: 6 }}>
															<Text fw={700} fz={'sm'} mt="xs" mb="md">
																Rating
															</Text>
															<div
																style={{
																	display: 'flex',
																	alignItems: 'center',
																	gap: '8px',
																}}
															>
																<Rating
																	value={
																		guideTrip.guideCompany.companyReviewData
																			.totalReviewAverage / 20
																	}
																	fractions={10}
																	readOnly
																	size="xs"
																/>
																<span>
																	Rated{' '}
																	{guideTrip.guideCompany.companyReviewData
																		.totalReviewAverage / 20}{' '}
																	on independent review sites.{' '}
																	<Link
																		href={`/guides/${sluggify(
																			guideTrip.guideCompany
																				?.companyName as string,
																		)}#reviews`}
																	>
																		View reviews
																	</Link>
																</span>
															</div>
														</Grid.Col>
													)}

													{!!guideTrip.guideCompany?.jsonProfile?.discounts && (
														<Grid.Col span={{ base: 12, md: 6 }}>
															<Alert title="Discount 👀">
																{guideTrip.guideCompany?.jsonProfile?.discounts}
															</Alert>
														</Grid.Col>
													)}
												</Grid>
												<div
													className=""
													style={{
														marginTop: '8px',
														textAlign: 'right',
													}}
												>
													<Button
														component="a"
														href={`/api/link?pGuideTripIdClicked=${guideTrip.id}`}
														target="_blank"
														rel="nofollow noreferrer ugc"
														onMouseDown={() => {
															AnalyitcsUtil.linkClickEvent()
														}}
														rightSection={<IconExternalLink size={14} />}
														variant="light"
													>
														View trip on{' '}
														{guideTrip.guideCompany?.jsonProfile.website
															?.replace('https://', '')
															.replace('www.', '')}
													</Button>
												</div>
											</>
										)}

										{/* // todo: images */}
									</Accordion.Panel>
								</Accordion.Item>
							</div>
						) : (
							<div
								key={idxGuideTrip}
								style={{
									position: 'relative',
									padding: '0px 32px 0px 0px',
									marginBottom: '1px',
									borderBottom: '0.0625rem solid #dee2e6',
									borderLeft: isPremium
										? '4px solid #FFD700'
										: '4px solid transparent',
								}}
							>
								<GuideOfferingOverview
									guideTrip={guideTrip}
									tripDetails={tripDetails}
								/>
							</div>
						)
					})}
			</Accordion>
			<Alert
				radius={'lg'}
				mt="lg"
				style={{
					textAlign: 'center',
					// backgroundColor: 'orange',
				}}
				variant="light"
				// variant="outline"
			>
				<div
					style={{
						display: 'inline-flex',
						flexDirection: 'row',
						alignItems: 'center',
						gap: '8px',
						// backgroundColor: 'red',
						// margin: '0 auto',
						// width: 'auto',
					}}
				>
					<div>
						{isShowingAll && (
							<Text>
								Showing all <strong>{totalTrips} Guides</strong> with this{' '}
								{version === Types.VERSION.PEAKS ? 'expedition' : 'trek'}
							</Text>
						)}
						{!isShowingAll && (
							<Text>
								<strong>
									Showing {TRIP_LIMIT} of {totalTrips}
								</strong>{' '}
								Guides
							</Text>
						)}
					</div>
					<div>
						{!isShowingAll && (
							<Button size={'xs'} onClick={() => setIsShowingAll(true)}>
								Show all {totalTrips} Guides
							</Button>
						)}
					</div>
				</div>
			</Alert>
		</div>
	)
}

const Guides = ({
	trip: {
		name,
		jsonData: { trekOrClimb },
		hasTopGuidesPage,
		slug,
	},
	trip,
	version,
	isAdSource,
}: Props) => {
	const authedUser = useAppSelector(Selectors.authedUser)
	const dispatch = useAppDispatch()
	const router = useRouter()

	const [isTableVisible, setIsTableVisible] = useState(
		!isAdSource || !!authedUser,
	)
	const guideTrips = trip.guideTrips ?? []

	if (guideTrips.length === 0) return <></>

	useEffect(() => {
		if (authedUser) {
			setIsTableVisible(true)
		} else {
			setIsTableVisible(false)
		}
	}, [authedUser])

	return (
		<div id="guides-wrapper" className="spacing-between-sections-above">
			<SectionHeader
				title={`${name} mountain guides`}
				section={Types.App.DataTypes.FeedbackSection.GUIDES}
			/>

			{guideTrips.length > 0 && (
				<>
					<Title order={3} mb="lg">
						Guide links
					</Title>
					<Text mb="xl">
						These guides offer organised{' '}
						{trekOrClimb === 'CLIMB' ? 'climbs' : 'treks'} for {name}. Compare
						each trip - pricing, reviews, and key details to help you choose the
						right guide. To explore more options / routes, check out our full{' '}
						<Link href="/guides">Guides Directory</Link>.
					</Text>

					{hasTopGuidesPage && (
						<Alert
							radius="md"
							mb="xl"
							styles={{
								root: {
									padding: '16px 24px',
								},
							}}
						>
							<div
								style={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
								}}
							>
								<div>
									<div style={{ fontSize: 22, marginBottom: 8 }}>
										Looking for the best {name} guides?
									</div>
									<div style={{ fontSize: 16 }}>
										We&apos;ve analyzed reviews and ratings to identify the best
										guides for {name}.
									</div>
								</div>
								<Link
									href={`/${router.query.routeType}/${router.query.continent}/${router.query.countries}/${router.query.slug}/best-${slug}-climbing-guides-2025`}
									className="plausible-event-name=Best-Guides-Link-Click"
								>
									<Button
										variant="filled"
										styles={{
											root: {
												padding: '8px 16px',
											},
										}}
									>
										View Best Guides →
									</Button>
								</Link>
							</div>
						</Alert>
					)}

					<Text mb="xl">
						We&apos;ve carefully selected these independent guides based on
						their expertise with {name}. Compare their offerings, from group
						sizes and equipment inclusion to trip duration and support services.
						Each guide brings their own unique approach, so you can find the
						perfect match for your adventure. Click through to see full details
						and connect directly with any guide that interests you.
					</Text>

					<div style={{ position: 'relative' }}>
						<div
							style={{
								opacity: isTableVisible ? 1 : 0.4,
								transition: 'all 0.5s ease',
								filter: isTableVisible ? 'none' : 'blur(2px)',
								pointerEvents: isTableVisible ? 'auto' : 'none',
								height: isTableVisible ? 'auto' : '400px',
								overflow: 'hidden',
							}}
						>
							<GuideTable guideTrips={guideTrips} version={version} />
						</div>
						{!isTableVisible && (
							<SignUpNudge
								context={{
									variant: 'GUIDES',
									contextMessage: `${name} (${trip.id})`,
									tripName: name,
									guideCount: guideTrips.length,
								}}
							/>
						)}
					</div>
				</>
			)}
		</div>
	)
}

export default Guides
